import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { AppConfigService } from '@core/appconfig.service';
import { SfxService } from '@core/sfx.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { ButtonComponent } from '@shared/button/button.component';
import { WistiaWindow } from '@shared/wistia/wistia-window.model';
import { Subscription } from 'rxjs';
import { WistiaService } from '../wistia.service';

@Component({
  selector: 'zbp-branded-wistia',
  templateUrl: './branded-wistia.component.html',
  styleUrls: ['./branded-wistia.component.scss'],
  imports: [CommonModule, ButtonComponent]
})
export class BrandedWistiaComponent implements OnInit, OnDestroy {

  @Input() isModal = false;
  @Input() wistiaCode: string;
  /** If loading in a modal, a name needs provided for the modal component for a11y. */
  @Input() accessibleName: string;
  /** Wistia player will be configured to work with the Asset Viewer */
  @Input() isViewer = false;
  @Input() isViewerFullScreen = false;
  @Input() viewerPlayerOptions?: any = {};
  /** Shows an error message in place of the player when the video can't load. */
  @Input() showErrorMessage = false;
  @Input() errorMessage = 'The video couldn\'t load. Please try again later.';
  @Output() videoEnded: EventEmitter<void> = new EventEmitter<void>();
  @Output() videoError: EventEmitter<void> = new EventEmitter<void>();
  @Output() videoReady: EventEmitter<void> = new EventEmitter<void>();
  @Output() closeButtonClicked: EventEmitter<void> = new EventEmitter<void>();
  @Output() videoDimensions: EventEmitter<{ width: number, height: number }> = new EventEmitter();

  readonly hasError = signal(false);

  private video: any;
  private subscriptions = new Subscription();

  constructor(
    private activeModal: NgbActiveModal,
    private wistia: WistiaService,
    private sfxService: SfxService,
    private appConfigService: AppConfigService,
  ) {
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit(): void {
    if (this.isViewer) {
      this.viewerPlayerOptions.playerColor = '#000000';

      this.subscriptions.add(this.wistia.loadScript()
        .subscribe((loaded) => {
          if (loaded) {
            this.initialize(false, this.viewerPlayerOptions);
          }
        }));
    } else {
      if (this.showErrorMessage) {
        if (!this.wistiaCode) {
          this.hasError.set(true);
          return;
        }
        this.subscriptions.add(this.wistia.loadError$.subscribe(() => this.hasError.set(true)));
      }

      this.subscriptions.add(this.wistia.loadScript()
        .subscribe((loaded) => {
          if (loaded) {
            // do not autoplay the video if in a modal
            this.initialize(!this.isModal);
          }
        }));
    }
  }

  dismiss(): void {
    if (this.isModal) {
      this.activeModal.dismiss();
      this.closeButtonClicked.emit();
    }
  }

  private initialize(autoPlay: boolean, playerOptions?: any) {
    const w: WistiaWindow = window;
    w._wq.push({
      id: this.wistiaCode,
      options: {
        autoPlay,
        ...playerOptions
      },
      onHasData: (video) => {
        this.video = video;
        this.video.chrome.focus();
        this.video.chrome.setAttribute('tabindex', '0');

        video.bind('play', () => {
          this.sfxService.stopQueue();
          if (this.appConfigService.isLocalUrl) {
            console.log(`'play' event fired for ${video.name()}`);
          }

          return video.unbind;
        });

        video.bind('end', () => {
          this.video.chrome.setAttribute('video', '-1');
          this.videoEnded.emit();
        });
        video.bind('error', () => {
          if (this.showErrorMessage) {
            this.hasError.set(true);
          }
          this.videoError.emit();
        });
      },
      onReady: (video: any) => {
        // Safe deep-navigation to Wistia's native uploaded file dimensions
        const media = video.data?.media || video._canonicalData?.media;
        if (media && media.assets) {
          // Find the original raw file configuration block
          const originalAsset = media.assets.find(
            a => a.type === 'OriginalFile' // Chrome
              || a.type === 'original' // Safari
          ) || media.assets[0];
          if (originalAsset) {
            this.videoDimensions.emit({
              // if the asset has 0 dimensions, default to 100x100 to make sure the video is visible
              width: (originalAsset.width > 0) ? originalAsset.width : 100,
              height: (originalAsset.height > 0) ? originalAsset.height : 100,
            });
          }
        }

        this.videoReady.emit();
      },
    });
  }

}
